"use client";
import React, { useState, useRef, useEffect, useCallback } from "react";
import cn from "@/utils/cn";
import TextComponent from "@/app/_components/TextComponent";
import {
  CheckMarkIcon,
  DashboardDropdownIcon,
  InputErrorIcon,
  ReorderIcon,
  DropDownEllipse,
  DropdownCreateNewIcon,
  DropdownRadioUnselectedIcon,
  DropdownRadioSelectedIcon,
  DropdownSaveIcon,
  DropdownArrowDownIcon,
  DropdownArrowUpIcon,
} from "@/assets/svg-icons/svg-icons";
import { useRouter, usePathname } from "next/navigation";
import axios from "axios";

interface DropdownItem {
  id: string;
  name: string;
  type?: "template" | "shared";
  isEditable?: boolean;
}

interface DropdownProps {
  title: string;
  selectedItemId: string | null;
  items: DropdownItem[];
  onSelect: (id: string) => void;
  onAdd?: (name: string) => string;
  onUpdate?: (id: string, name: string) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onReorder?: (newOrder: DropdownItem[]) => void;
  IconOpen: any;
  IconClosed: any;
  placeholder?: string;
  minNameLength?: number;
  showCreateOption?: boolean;
  invertColors?: boolean;
  className?: string;
  variant?: "default" | "simple";
  label?: string;
  labelClassName?: string;
  buttonClassName?: string;
  modalPosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  modalClassName?: string;
  error?: any;
  truncateLength?: number;
  buttonTextClass?: string;
  disabled?: boolean;
  hideDeleteForSelected?: boolean; // New prop to control delete visibility for selected item
}

const ReusableDropdown: React.FC<DropdownProps> = ({
  title,
  selectedItemId,
  items,
  onSelect,
  onAdd,
  onUpdate,
  onDelete,
  onDuplicate,
  onReorder,
  IconOpen,
  IconClosed,
  minNameLength = 1,
  showCreateOption,
  invertColors,
  className = "",
  variant = "default",
  label = "",
  labelClassName = "",
  buttonClassName = "",
  modalPosition = "bottom-right",
  truncateLength = 0,
  buttonTextClass = "",
  error,
  disabled = false,
  hideDeleteForSelected = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItemName, setEditingItemName] = useState("");
  const [showOptionsForId, setShowOptionsForId] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [reorderMode, setReorderMode] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsModalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const pathName = usePathname();

  const selectedItem =
    items.find((item) => item.id === selectedItemId)?.name || "";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        resetState();
      }

      // Close options modal when clicking outside
      if (
        optionsModalRef.current &&
        !optionsModalRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest(".options-trigger")
      ) {
        setShowOptionsForId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if ((isCreatingNew || editingItemId) && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreatingNew, editingItemId]);

  const resetState = useCallback(() => {
    setIsOpen(false);
    setIsCreatingNew(false);
    setNewItemName("");
    setShowOptionsForId(null);
    setEditingItemId(null);
    setEditingItemName("");
    setDraggedIndex(null);
    setDragOverIndex(null);
    setReorderMode(false);
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleItemSelect = useCallback(
    (id: string) => {
      // Don't select if in reorder mode
      if (reorderMode) return;

      onSelect(id);
      setShowOptionsForId(null);
      resetState();
    },
    [onSelect, resetState, reorderMode]
  );

  const handleCreateNewClick = () => {
    if (variant === "default" && onAdd) {
      setIsCreatingNew(true);
      setNewItemName("");
      setShowOptionsForId(null);
    }
  };

  const handleNewItemNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewItemName(e.target.value);
  };

  const handleNewItemSubmit = () => {
    if (
      onAdd &&
      newItemName.trim().length >= minNameLength &&
      newItemName.trim() !== title
    ) {
      const newItemId = onAdd(newItemName.trim());
      handleItemSelect(newItemId);
      setIsCreatingNew(false);
      setNewItemName("");
    }
  };

  const handleEditItem = (
    id: string,
    currentName: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    if (variant === "default" && onUpdate) {
      setEditingItemId(id);
      setEditingItemName(currentName);
      setShowOptionsForId(null);
    }
  };

  const handleEditItemNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingItemName(e.target.value);
  };

  const handleEditItemSubmit = () => {
    if (
      editingItemId &&
      editingItemName.trim().length >= minNameLength &&
      onUpdate
    ) {
      onUpdate(editingItemId, editingItemName.trim());
      setEditingItemId(null);
      setEditingItemName("");
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!onDelete) {
      return;
    }

    try {
      // Await the onDelete callback (allowing for async parents)
      await onDelete(id);
      // Parent is now responsible for managing selection and data synchronization
    } catch (error) {
      console.error("Error in delete callback:", error);
      // Handle the case where deletion is prevented
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        console.log("Cannot delete the last dashboard");
        // You could show a toast notification here if needed
      }
    } finally {
      setShowOptionsForId(null);
    }
  };

  const handleEnableReorderMode = () => {
    setReorderMode(true);
    setShowOptionsForId(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent, isEditing: boolean) => {
    if (e.key === "Enter") {
      isEditing ? handleEditItemSubmit() : handleNewItemSubmit();
    }
  };

  const handleOptionsClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setShowOptionsForId(showOptionsForId === id ? null : id);
  };

  const truncateText = (text: string, maxLength: number) => {
    if (maxLength === 0) return text;
    return text?.length > maxLength
      ? text?.substring(0, maxLength) + "..."
      : text;
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (!reorderMode) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "0.5";
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "1";
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    if (!reorderMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (draggedIndex === null || draggedIndex === index) {
      return;
    }

    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    if (!reorderMode) return;
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Create new array with reordered items
    const newItems = [...items];
    const [draggedItem] = newItems.splice(draggedIndex, 1);
    newItems.splice(dropIndex, 0, draggedItem);

    // Update Zustand store and backend via onReorder callback
    if (onReorder) {
      onReorder(newItems);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const scrollbarClass = "overflow-y-auto custom-scrollbar";

  return (
    <div className={`relative select-none  ${className}`} ref={dropdownRef}>
      {label && (
        <div className="flex items-center space-x-2">
          <TextComponent
            type="h4"
            className={cn(
              "mb-[6.5px] font-medium xl:mb-[10px] text-kd-secondary-2",
              labelClassName
            )}
          >
            {label}
          </TextComponent>
          {error && (
            <div className="mb-[6.5px] flex items-center space-x-1 xl:mb-[10px]">
              <InputErrorIcon className="max-xl:size-1.5" />

              <TextComponent type="caption" className="text-[#FA5A7D]">
                {error}
              </TextComponent>
            </div>
          )}
        </div>
      )}

      <div
        onClick={toggleDropdown}
        className={cn(
          "flex items-center space-x-2 justify-between px-4 py-2 h-[60px] focus:outline-none",
          "xl:text-[10.6px]  xl:h-[60px] 2xl:text-base",
          "h-[45px] text-[8px] flex rounded-xl xl:rounded-[18px]",
          "2xl:w-full w-full xl:w-full md:w-full",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
          invertColors ? "border" : "",
          disabled
            ? "bg-kd-secondary-7 text-kd-secondary-4"
            : variant != "simple" &&
            (invertColors
              ? isOpen
                ? "bg-kd-secondary-7 text-kd-secondary-1"
                : "bg-kd-lte-blue text-white"
              : isOpen
                ? "bg-kd-secondary-7 text-kd-secondary-1 border border-kd-lte-blue"
                : "bg-kd-secondary-7 text-kd-secondary-1"),
          !disabled && variant === "simple" && "bg-kd-secondary-7",
          !disabled &&
          (invertColors
            ? "hover:border-kd-lte-blues"
            : "hover:border-kd-lte-blue border-transparent border"),
          buttonClassName,
          error && !disabled ? "border border-[#FA5A7D]" : ""
        )}
      >
        <div
          className={cn(
            "size-5 xl:size-[26px]",
            variant === "simple" && "hidden"
          )}
        >
          {disabled ? (
            <DashboardDropdownIcon className="size-full text-kd-secondary-4" />
          ) : invertColors ? (
            isOpen ? (
              <IconOpen className={"size-full text-kd-lte-blue"} />
            ) : (
              <DashboardDropdownIcon className="size-full" />
            )
          ) : isOpen ? (
            <IconClosed className={"size-full text-kd-lte-blue"} />
          ) : (
            <IconOpen className={"size-full text-kd-lte-blue"} />
          )}
        </div>
        <TextComponent
          type="h4"
          className={cn(
            "grow text-nowrap text-left md:flex min-w-0",
            variant === "default" && "hidden",
            buttonTextClass
          )}
        >
          <div className="hidden 2xl:block truncate">
            {selectedItem ? (
              truncateText(selectedItem, truncateLength)
            ) : (
              <span className={cn("text-[#8E8E93]", buttonTextClass)}>
                {title}
              </span>
            )}
          </div>
          <div className="block 2xl:hidden truncate">
            {selectedItem ? (
              truncateText(selectedItem, truncateLength)
            ) : (
              <span className={cn("text-[#8E8E93]", buttonTextClass)}>
                {title}
              </span>
            )}
          </div>
        </TextComponent>

        {disabled ? (
          <DropdownArrowDownIcon className="text-kd-secondary-4" />
        ) : isOpen ? (
          <DropdownArrowUpIcon
            className={cn(
              variant !== "simple" &&
              (!invertColors
                ? isOpen
                  ? "text-kd-lte-blue"
                  : "text-kd-lte-blue"
                : isOpen
                  ? "text-kd-lte-blue"
                  : "text-white")
            )}
          />
        ) : (
          <DropdownArrowDownIcon
            className={cn(
              variant !== "simple"
                ? !invertColors
                  ? "text-kd-lte-blue"
                  : "text-white"
                : "text-kd-secondary-1"
            )}
          />
        )}
      </div>

      {isOpen && !disabled && (
        <div
          className={cn(
            "absolute z-30 mt-2 xl:w-[280px] w-[210px]",
            modalPosition === "bottom-right" ? "right-0" : "left-0"
          )}
        >
          <div className="overflow-hidden rounded-2xl border border-kd-lte-blue bg-white shadow-lg xl:max-h-80">
            <div
              className={cn(
                "max-h-80 overflow-y-scroll rounded-2xl overflow-hidden",
                scrollbarClass
              )}
            >
              <div className="">
                {variant === "default" && showCreateOption && onAdd && (
                  <div
                    className="sticky top-0 z-30 flex items-center border-b border-dashed border-gray-300 bg-white p-4 hover:bg-kd-secondary-6"
                    onClick={isCreatingNew ? undefined : handleCreateNewClick}
                  >
                    <div className="mr-[7.5px] xl:mr-3">
                      <DropdownCreateNewIcon className="size-3 text-kd-lte-blue xl:size-4" />
                    </div>
                    {isCreatingNew ? (
                      <>
                        <input
                          ref={inputRef}
                          type="text"
                          value={newItemName}
                          onChange={handleNewItemNameChange}
                          onKeyDown={(e) => handleKeyPress(e, false)}
                          className="w-full bg-transparent focus:outline-none text-[10.5px] xl:text-[14px] font-normal text-kd-secondary-1 placeholder:text-kd-secondary-3"
                          placeholder={`Enter ${title.toLowerCase()} name`}
                        />
                        <div
                          className="ml-2 cursor-pointer"
                          onClick={handleNewItemSubmit}
                        >
                          <DropdownSaveIcon className="size-3 text-kd-lte-blue xl:size-4" />
                        </div>
                      </>
                    ) : (
                      <TextComponent
                        type="caption"
                        className="cursor-pointer text-[#8E8E93]"
                      >
                        Create New {title}
                      </TextComponent>
                    )}
                  </div>
                )}

                {items.map((item, index) => (
                  <div
                    key={index}
                    draggable={
                      reorderMode &&
                      editingItemId !== item.id &&
                      onReorder !== undefined
                    }
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    className={cn(
                      "group relative flex items-center justify-between p-4 transition-colors hover:bg-kd-secondary-6 hover:text-kd-lte-blue text-[#96A5B8]",
                      reorderMode
                        ? "cursor-grab active:cursor-grabbing"
                        : "cursor-pointer",
                      dragOverIndex === index && draggedIndex !== index
                        ? "border-t-2 border-kd-lte-blue"
                        : "",
                      draggedIndex === index ? "opacity-50" : "opacity-100"
                    )}
                    onClick={() =>
                      editingItemId !== item.id && handleItemSelect(item.id)
                    }
                  >
                    {/* Show drag handle in reorder mode, otherwise show radio button */}
                    <div className="mr-2">
                      {reorderMode && onReorder ? (
                        <div
                          className="cursor-grab hover:bg-gray-200 active:cursor-grabbing"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <ReorderIcon className="text-kd-lte-blue" />
                        </div>
                      ) : (
                        <div className="mr-[1.5px] xl:mr-1">
                          {item.id === selectedItemId ? (
                            <DropdownRadioSelectedIcon className="size-3 text-kd-lte-blue xl:size-4" />
                          ) : (
                            <DropdownRadioUnselectedIcon className="size-3 text-[#96A5B8] group-hover:text-[#336DFF] xl:size-4" />
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex grow items-center">
                      {editingItemId === item.id &&
                        variant === "default" &&
                        onUpdate ? (
                        <input
                          ref={inputRef}
                          type="text"
                          value={editingItemName}
                          onChange={handleEditItemNameChange}
                          onKeyDown={(e) => handleKeyPress(e, true)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full bg-transparent focus:outline-none text-[10.5px] xl:text-[14px] font-normal text-kd-secondary-1 placeholder:text-kd-secondary-3"
                        />
                      ) : (
                        <TextComponent
                          type="caption"
                          className={cn(
                            "flex items-center capitalize",
                            item.id === selectedItemId && !reorderMode
                              ? "text-[#05004E] font-medium"
                              : ""
                          )}
                        >
                          {truncateText(item.name, 33)}{" "}
                        </TextComponent>
                      )}
                    </div>

                    {/* Hide options menu in reorder mode */}
                    {!reorderMode &&
                      variant === "default" &&
                      onUpdate &&
                      onDelete && (
                        <div
                          className="flex items-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {item?.type === "shared" && (
                            <div className="mx-1 flex h-3 w-fit items-center justify-center rounded-[4px] border-[1.4px] border-kd-lte-blue px-1 text-[5px] capitalize text-kd-lte-blue xl:h-[18px] xl:rounded-md xl:px-[6px] xl:text-[7.5px]">
                              {item?.type === "shared" && `${item?.type}`}
                            </div>
                          )}
                          {editingItemId === item.id && (
                            <div
                              className="cursor-pointer"
                              onClick={handleEditItemSubmit}
                            >
                              <DropdownSaveIcon className="size-3 text-kd-lte-blue xl:size-4" />
                            </div>
                          )}
                          {/* Hide ellipsis when editing */}
                          {editingItemId !== item.id && (
                            <button
                              onClick={(e) => handleOptionsClick(e, item.id)}
                              className="px-2 text-[#96A5B8] hover:text-gray-600"
                            >
                              <DropDownEllipse className="h-[18px] w-1" />
                            </button>
                          )}

                          {/* popup options */}
                          {showOptionsForId === item.id && (
                            <div className="fixed z-20" ref={optionsModalRef}>
                              <div className="absolute -top-4 right-2 w-[123px] origin-top-right overflow-hidden rounded-lg border border-kd-lte-blue bg-[#F8F9FA] shadow-lg">
                                {pathName !== "/dashboard/edit" && (
                                  <button
                                    onClick={() => {
                                      handleItemSelect(item.id);
                                      router.push("/dashboard/edit");
                                    }}
                                    className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                                  >
                                    Edit
                                  </button>
                                )}
                                {item?.type !== "shared" && (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditItem(item.id, item.name, e);
                                      }}
                                      className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                                    >
                                      Rename
                                    </button>
                                    {onDuplicate && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onDuplicate(item.id);
                                          setShowOptionsForId(null);
                                        }}
                                        className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                                      >
                                        Duplicate
                                      </button>
                                    )}
                                    {onReorder && (
                                      <button
                                        onClick={handleEnableReorderMode}
                                        className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                                      >
                                        Reorder
                                      </button>
                                    )}
                                    {(!hideDeleteForSelected ||
                                      item.id !== selectedItemId) && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteItem(item.id);
                                          }}
                                          disabled={items.length <= 1}
                                          className={cn(
                                            "block w-full border-t border-dashed border-gray-200 px-4 py-2 text-left text-xs",
                                            items.length <= 1
                                              ? "cursor-not-allowed text-gray-400 bg-gray-50"
                                              : "text-[#FA5A7D] hover:bg-white cursor-pointer"
                                          )}
                                        >
                                          Delete
                                        </button>
                                      )}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReusableDropdown;
